import { IBackStory } from "@src/components/Backstory";
import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import DialogModal from "@src/components/Dialog";
import BackstoryModal from "@src/components/modals/BackstoryModal";
import { truncate } from "lodash";
import flowerBackground from "../../../../public/images/bw-flower.jpeg";

const Backstory: React.FC<IBackStory> = ({ eventId, backStoryData }) => {
  const text = "The Journey to 'I Do'";
  const [displayed, setDisplayed] = useState("");
  const [typing, setTyping] = useState(true);
  const [inView, setInView] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const containerRef = useRef(null);

  const stories = backStoryData?.getBackStory ?? [];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !inView) {
          setInView(true);
        }
      },
      {
        threshold: 0.3,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    const currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [inView]);

  useEffect(() => {
    if (!inView) return;

    setDisplayed("");
    setTyping(true);
    let i = 0;
    const interval = setInterval(() => {
      setDisplayed((prev) => prev + text[i]);
      i++;

      if (i >= text.length) {
        clearInterval(interval);
        setTyping(false);
      }
    }, 30);
    return () => clearInterval(interval);
  }, [inView]);

  useEffect(() => {
    if (stories.length <= 1 || isModalOpen) return;

    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % stories.length);
        setIsAnimating(false);
      }, 300);
    }, 6000); // 6 seconds per slide

    return () => clearInterval(interval);
  }, [stories.length, isModalOpen]);

  const handleSlideChange = (index: number) => {
    if (index === currentSlide || isAnimating) return;

    setIsAnimating(true);
    setTimeout(() => {
      setCurrentSlide(index);
      setIsAnimating(false);
    }, 300);
  };

  const handlePrevSlide = () => {
    const prevIndex = (currentSlide - 1 + stories.length) % stories.length;
    handleSlideChange(prevIndex);
  };

  const handleNextSlide = () => {
    const nextIndex = (currentSlide + 1) % stories.length;
    handleSlideChange(nextIndex);
  };

  const currentStory = stories[currentSlide];

  return stories.length > 0 ? (
    <section className="w-full py-6 md:py-12 bg-[#E8EEF6]">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div ref={containerRef} className="text-center mb-8 lg:mb-12">
          <h2 className="font-bauerBodoni text-2xl md:text-4xl font-light text-[#071A2F] uppercase tracking-wider">
            {displayed}
            {typing && <span className="animate-pulse">|</span>}
          </h2>
        </div>

        {/* Single Story Slide */}
        {currentStory && (
          <div
            className={`transition-all duration-500 ${
              isAnimating ? "opacity-0 scale-95" : "opacity-100 scale-100"
            }`}
          >
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 items-center justify-center">
              {/* Left Column - Image */}
              <div className="w-full lg:w-[40%] flex justify-center">
                <div className="w-80 h-96 lg:w-96 lg:h-[480px] rounded-lg overflow-hidden bg-gray-300 relative shadow-2xl">
                  <Image
                    placeholder="blur"
                    loading="eager"
                    layout="fill"
                    blurDataURL={`data:image/svg+xml;base64,${toBase64(
                      shimmer(700, 475)
                    )}`}
                    src={currentStory?.image || flowerBackground}
                    alt={currentStory?.title || "Wedding photo"}
                    className="object-cover"
                    sizes="(max-width: 768px) 320px, 384px"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>
              </div>

              <div className="w-full lg:w-[40%] flex justify-center lg:justify-start">
                <div className="border border-[#fff] p-2 w-full max-w-[560px]">
                  <div className="bg-white px-6 py-8 md:px-8 md:py-10 flex flex-col items-center lg:items-start text-center lg:text-left">
                    <h3 className="text-[#b18958] text-2xl lg:text-4xl font-normal tracking-widest mb-4 uppercase font-bauerBodoni">
                      {currentStory?.title || "Our Story"}
                    </h3>

                    <p className="text-[#071A2F] text-base  font-normal text-justify font-openSans leading-relaxed mb-6 max-w-md">
                      {truncate(currentStory?.story, {
                        length: 500,
                      }) || "Our love story begins here..."}
                    </p>
                    <p className="text-[#b18958] text-sm text-sm font-normal tracking-widest mb-4 uppercase font-bauerBodoni">
                      {currentStory?.role}
                    </p>

                    <DialogModal
                      trigger={
                        <button className="border px-8 py-3 text-sm tracking-wider font-openSans bg-[#031d40] hover:bg-[#031d40]/90 text-[#b18958] transition-colors duration-300 rounded-2xl transform hover:scale-105">
                          CONTINUE READING
                        </button>
                      }
                      onOpenChange={setIsModalOpen}
                    >
                      <BackstoryModal
                        title={currentStory?.title ?? ""}
                        story={currentStory?.story ?? ""}
                        role={currentStory?.role ?? ""}
                      />
                    </DialogModal>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {stories.length > 1 && (
          <div className="mt-12 flex flex-col items-center gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={handlePrevSlide}
                className="w-[50px] h-[50px] rounded-full bg-[#031d40] flex items-center justify-center transition-all duration-200 hover:scale-105 hover:bg-[#031d40]/90 active:scale-95"
                aria-label="Previous story"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M15 5L8 12L15 19"
                    stroke="#b18958"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <button
                onClick={handleNextSlide}
                className="w-[50px] h-[50px] rounded-full bg-[#031d40] flex items-center justify-center transition-all duration-200 hover:scale-105 hover:bg-[#031d40]/90 active:scale-95"
                aria-label="Next story"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M9 5L16 12L9 19"
                    stroke="#b18958"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {/* <div className="flex justify-center gap-3">
              {stories.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleSlideChange(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentSlide
                      ? "bg-[#071A2F] scale-125 shadow-lg"
                      : "bg-[#071A2F]/40 hover:bg-[#071A2F]/70"
                  }`}
                  aria-label={`View story ${index + 1}`}
                />
              ))}
            </div> */}
          </div>
        )}
      </div>
    </section>
  ) : (
    <></>
  );
};

export default Backstory;
